import { useEffect, useRef } from 'react'
import { useNotifications } from '../context/NotificationContext'
import type { AppNotification } from '../types/notification'

export type SupportRealtimeHandlers = {
  onTicketCreated?: (supportRequestId: string, notification: AppNotification) => void
  onTicketReply?: (supportRequestId: string, notification: AppNotification) => void
}

function getSupportRequestId(notification: AppNotification): string | null {
  const raw = notification.data?.support_request_id

  return typeof raw === 'string' && raw.length > 0 ? raw : null
}

/**
 * Live-updates Support pages from the same `NotificationCreated` stream as the bell/toast.
 * Does not open its own Echo channel — uses NotificationContext.subscribe.
 */
export function useSupportRealtime(handlers: SupportRealtimeHandlers): void {
  const { subscribe } = useNotifications()
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers

  useEffect(() => {
    return subscribe((notification) => {
      const supportRequestId = getSupportRequestId(notification)

      if (!supportRequestId) {
        return
      }

      if (notification.type === 'support_created') {
        handlersRef.current.onTicketCreated?.(supportRequestId, notification)
        return
      }

      if (notification.type === 'support_reply') {
        handlersRef.current.onTicketReply?.(supportRequestId, notification)
      }
    })
  }, [subscribe])
}
